import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { fetchLogs } from "../../Controllers/logController";
import moment from "moment";
import { useError } from "../../Context/errorContext";
import { statuses } from "../helpers";

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    user: "",
    location: "",
    dateFrom: null,
    dateTo: null,
  });
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
  });
  const [limit, setLimit] = useState(25);
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { setAlert } = useError();

  const handleOpenModal = (log) => {
    setSelectedLog(log);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
    setModalOpen(false);
  };

  useEffect(() => {
    fetchLogs(1, filters, setLogs, setPagination, setAlert);
  }, []);

  return (
    <Paper sx={{ padding: 3 }}>
      <Typography variant="h6" gutterBottom>
        Server Logs
      </Typography>

      {/* Filters Section */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Box display="flex" gap={2} mb={2}>
          <TextField
            label="User"
            value={filters.user}
            onChange={(e) => setFilters({ ...filters, user: e.target.value })}
          />
          <TextField
            label="Location"
            value={filters.location}
            onChange={(e) =>
              setFilters({ ...filters, location: e.target.value })
            }
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date From"
              value={filters.dateFrom}
              onChange={(newValue) =>
                setFilters({ ...filters, dateFrom: newValue })
              }
              format="DD.MM.YYYY" // Display in DD.MM.YYYY format
            />
            <DatePicker
              label="Date To"
              value={filters.dateTo}
              onChange={(newValue) =>
                setFilters({ ...filters, dateTo: newValue })
              }
              format="DD.MM.YYYY"
            />
          </LocalizationProvider>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="limit-select-label">Rows per page</InputLabel>
            <Select
              labelId="limit-select-label"
              id="limit-select"
              value={limit}
              label="Rows per page"
              onChange={(e) => {
                setLimit(e.target.value);
                fetchLogs(1, e.target.value); // reset to page 1
              }}
            >
              {[
                statuses.DRAFT,
                statuses.IN_PROPOSAL_DEVELOPMENT,
                statuses.PREPARING_FOR_SIGNOFF,
                statuses.FINISHED,
              ].map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={() => fetchLogs(1)}
            sx={{ whiteSpace: "nowrap" }}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Timestamp</strong>
            </TableCell>
            <TableCell>
              <strong>Message</strong>
            </TableCell>
            <TableCell>
              <strong>Location</strong>
            </TableCell>
            <TableCell>
              <strong>User</strong>
            </TableCell>
            <TableCell>
              <strong>Actions</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log, i) => (
            <TableRow key={i}>
              <TableCell>
                {moment(log.timestamp).format("DD.MM.YYYY HH:mm:ss")}
              </TableCell>
              <TableCell>{log.message}</TableCell>
              <TableCell>{log.location}</TableCell>
              <TableCell>{log.user}</TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleOpenModal(log)}
                >
                  View Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <Box mt={2} display="flex" justifyContent="center">
        <Pagination
          count={pagination.totalPages}
          page={pagination.currentPage}
          onChange={(_, page) => fetchLogs(page)}
        />
      </Box>

      {/* Modal Dialog */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Error Details</DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Time:</strong>{" "}
                {moment(selectedLog.timestamp).format("YYYY-MM-DD HH:mm:ss")}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>User:</strong> {selectedLog.user || "—"}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Location:</strong> {selectedLog.location}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>URL:</strong> {selectedLog.url}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Message:</strong>
              </Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                {selectedLog.message}
              </Typography>
              {selectedLog.stack && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Stack Trace:</strong>
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      padding: 2,
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      fontSize: "0.875rem",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {selectedLog.stack}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminLogs;
